/**
 * frontend/src/pages/valuation/ValuationNormalize.tsx
 *
 * VAL-U2C-SCREEN-1 — "Review and adjust", between Financials and Intake. The
 * signer sees each year side by side, adds add-backs with a reason, types total
 * assets and liabilities, sees the normalized earnings and the rule, and presses
 * Apply to valuation. Nothing is computed here, and nothing is shown that the
 * server did not return: every save re-reads the view (the stored value is what
 * the screen shows). No browser storage.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { fetchFinancials, type FinancialsRow } from '@/api/valuationFinancials'
import {
  applyNormalization, fetchNormalization, removeAddBack, saveAddBack, saveBalanceSheet,
  type AddBackBody, type ApplyResponse, type NormalizationView,
} from '@/api/valuationNormalization'
import { formatApiError } from '@/utils/httpErrors'
import { ValuationStepper, basisLabel } from './_shared'
import { NormalizeYearTable, money } from './_NormalizeYearTable'
import { NormalizeAddBacks } from './_NormalizeAddBacks'
import { NormalizeBalanceSheet } from './_NormalizeBalanceSheet'

const TWO_YEARS = 'Upload at least two fiscal years before applying.'

export default function ValuationNormalize() {
  const { valuationId } = useParams<{ valuationId: string }>()
  const [view, setView] = useState<NormalizationView | null>(null)
  const [rows, setRows] = useState<FinancialsRow[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState<ApplyResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)

  const reload = useCallback(async (id: string) => { setView(await fetchNormalization(id)) }, [])

  useEffect(() => {
    if (!valuationId) return
    let live = true
    Promise.all([fetchNormalization(valuationId), fetchFinancials(valuationId)])
      .then(([n, f]) => { if (live) { setView(n); setRows(f.rows) } })
      .catch((err: unknown) => { if (live) setLoadError(formatApiError(err)) })
    return () => { live = false }
  }, [valuationId])

  /** One server call at a time; the view is re-read after every change. */
  async function run(work: (id: string) => Promise<void>) {
    if (!valuationId || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      await work(valuationId)
    } catch (err: unknown) {
      const message = formatApiError(err)
      setError(message)
      toast.error(message)
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const add = (body: AddBackBody) => run(async id => { await saveAddBack(id, body); setApplied(null); await reload(id) })
  const remove = (addBackId: string) => run(async id => { await removeAddBack(id, addBackId); setApplied(null); await reload(id) })
  const saveSheet = (body: { total_assets: number; total_liabilities: number; as_of_year: number | null }) =>
    run(async id => { await saveBalanceSheet(id, body); setApplied(null); await reload(id) })
  const apply = () => run(async id => { setApplied(await applyNormalization(id)) })

  if (loadError) {
    return (
      <div className="mx-auto max-w-7xl p-6">
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{loadError}</div>
      </div>
    )
  }
  if (!view) return <div className="mx-auto max-w-7xl p-6 text-sm text-gray-500">Loading…</div>

  const result = 'error' in view.result ? null : view.result
  const resultError = 'error' in view.result ? view.result.error : null
  const canApply = view.years.length >= 2 && result !== null
  const years = [...view.years].sort((a, b) => a - b)

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Review and adjust</h1>
        <p className="mt-1 text-sm text-gray-500">
          Check each year, add back anything that is not part of normal earnings (with the reason), and apply the result to the valuation.
        </p>
      </div>
      <ValuationStepper current="adjust" engagementId={valuationId} />

      {view.years.length === 0 ? (
        <p data-testid="no-statements" className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
          No statements have been read for this engagement. Financials come from an uploaded file: upload statements on{' '}
          <Link to={`/valuation/${valuationId}/financials`} className="text-blue-700 underline">Financials</Link> to review them here.
        </p>
      ) : (
      <>
      <NormalizeYearTable rows={rows} normalized={result?.years ?? []} />
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      <NormalizeAddBacks
        years={years}
        addBacks={view.add_backs}
        removedCount={view.removed_count}
        busy={busy}
        onAdd={add}
        onRemove={remove}
      />
      <NormalizeBalanceSheet
        key={view.balance_sheet ? 'stored' : 'empty'}
        stored={view.balance_sheet}
        asOfYear={years[years.length - 1]}
        busy={busy}
        onSave={saveSheet}
      />

      <section className="space-y-2 rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-gray-900">Result</h2>
        {result ? (
          <>
            <p className="text-sm text-gray-700">{result.rule_text}</p>
            <p className="text-sm text-gray-700">Basis: {basisLabel(result.basis)}</p>
            <p className="text-lg font-semibold text-gray-900">
              Normalized earnings: <span data-testid="normalized-earnings">{money(result.normalized_earnings)}</span>
            </p>
          </>
        ) : (
          <p className="text-sm text-amber-800">{resultError}</p>
        )}
      </section>

      <div className="space-y-2">
        <button
          type="button"
          disabled={!canApply || busy}
          onClick={() => void apply()}
          className="rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
        >
          Apply to valuation
        </button>
        {view.years.length < 2 && <p className="text-sm text-amber-800">{TWO_YEARS}</p>}
        {applied && (
          <p className="text-sm text-green-800">
            Applied to the valuation: {basisLabel(applied.applied.basis)} {money(applied.applied.normalized_earnings)}.
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Link to={`/valuation/${valuationId}/intake`} className="rounded bg-[#0d1f3c] px-4 py-2 text-sm font-medium text-white">
          Continue to Intake
        </Link>
        <Link to={`/valuation/${valuationId}/financials`} className="text-sm text-blue-700 underline">Back to Financials</Link>
      </div>
      </>
      )}
    </div>
  )
}
